import type { ReactNode } from 'react'

export enum SummaryMode {
  Latest = 'latest',
  Total = 'total',
}

export enum MetricUnit {
  Percentage = 'percentage',
}

/**
 * Which period(s) the chart renders. The string values double as the
 * `viewMode` keys understood by buildChartSeries, so they must stay in sync.
 */
export enum ViewMode {
  Primary = 'primary',
  Comparison = 'comparison',
  Both = 'both',
}

/**
 * Narrows an untyped string (e.g. a control's `onChange` value) to a ViewMode.
 * Shared so the period toggle and its collapsed dropdown stay in sync.
 */
export function isViewMode(value: string): value is ViewMode {
  return (
    value === ViewMode.Primary ||
    value === ViewMode.Comparison ||
    value === ViewMode.Both
  )
}

export interface TimeSeriesDataPoint {
  date: string
  value?: number | null
  /** Real underlying date for a comparison-period point that has been
   * positionally re-aligned onto the primary axis. Drives tooltip + axis labels. */
  originalDate?: string
  /** How `value` was derived, when the source series provides it (normalized
   * all-channels data): `observed` = real reading, `computed` = carried forward
   * from an earlier reading (value null), `missing` = leading-edge gap (value
   * null). Absent for series that aren't normalized (e.g. single-channel), where
   * consumers fall back to carry-forward inference. Values mirror the GraphQL
   * `TimeSeriesPointSource` enum, so codegen results assign directly.
   * @see resolveValueAtTimestamp, getTooltipPoint */
  source?: 'observed' | 'computed' | 'missing'
}

export interface MetricSeries {
  key: string
  title: string
  icon: ReactNode
  data: TimeSeriesDataPoint[]
  color: string
  /** How the summary value is computed:
   * - Latest: show the last data point value and change from first (default, for cumulative metrics like Followers)
   * - Total: show the sum of all values (for count-per-day metrics like Posts)
   */
  summaryMode?: SummaryMode
  /** When set, values are rendered with a unit suffix (e.g. '%' for growth
   * rates). Drives Y-axis tick formatting, tooltip values, and the export
   * snapshot's heading. Series-level so a chart with mixed units still
   * formats each axis correctly. */
  unit?: MetricUnit
  /** When true, the line is rendered with a dashed stroke. Used to visually
   * distinguish comparison-period series from the primary series. */
  dashed?: boolean
  /** Optional comparison-period data for this metric. When set, the chart
   * renders an additional dashed line positionally aligned with `data`. */
  comparisonData?: TimeSeriesDataPoint[]
}
